import React, { useState, useEffect, useRef } from 'react';
import { useCart, useSyncCartBusiness } from '@/contexts/CartContext';
import { buildAssistantAnalyticsBase } from '@/lib/assistantAnalytics';
import { createEmptySessionMemory } from './assistantChat/assistantChatSession';
import { useAssistantChatLoad } from './assistantChat/useAssistantChatLoad';
import { bindAssistantChatMessages } from './assistantChat/bindAssistantChatMessages';
import { bindAssistantChatCheckout } from './assistantChat/bindAssistantChatCheckout';
import { bindAssistantChatMenus } from './assistantChat/bindAssistantChatMenus';
import { bindAssistantChatCart } from './assistantChat/bindAssistantChatCart';
import { bindAssistantChatSearch } from './assistantChat/bindAssistantChatSearch';

export function useAssistantChat({ routeBusinessId, navigate }) {
  const routeBid = String(routeBusinessId ?? '').trim();
  const [businessId, setBusinessId] = useState(() => routeBid || null);
  const [businessIdReady, setBusinessIdReady] = useState(() => !!routeBid);

  const messagesEndRef = useRef(null);
  const messagesRef = useRef([]);
  const silentNudgeTimeoutRef = useRef(null);
  const lastSuggestedProductIdsRef = useRef([]);
  const sessionMemoryRef = useRef(createEmptySessionMemory());
  const handleMainChoiceRef = useRef(() => {});
  const proceedToCheckoutRef = useRef(() => {});
  const showMainMenuRef = useRef(() => {});
  const demoAssistantUsedRef = useRef(false);
  const freeTextInputRef = useRef(null);
  const pendingProductsRef = useRef([]);
  const productFlowRef = useRef(null);
  const wireRef = useRef({});

  const {
    addToCart,
    addClubRewardToCart,
    mergeAddOnsIntoLatestLineForProduct,
    getCartCount,
    getCartTotal,
    getCartSnapshot,
    clearCart,
    cart,
    removeFromCart,
    updateQuantity,
    removeAddOnFromCartLine,
    setBusiness,
  } = useCart();

  useSyncCartBusiness(businessId);

  const [profileDraft, setProfileDraft] = useState(null);
  const [assistantCheckoutOpen, setAssistantCheckoutOpen] = useState(false);
  const [checkoutBootstrap, setCheckoutBootstrap] = useState(null);
  const [menuData, setMenuData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [currentStep, setCurrentStep] = useState('welcome');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [menuReloadKey, setMenuReloadKey] = useState(0);
  const [lastOrder, setLastOrder] = useState(null);
  const [customerId, setCustomerId] = useState(null);
  const [repeatOrderAvailable, setRepeatOrderAvailable] = useState(false);
  const [benefitsProgressSnapshot, setBenefitsProgressSnapshot] = useState(null);
  const [freeTextInput, setFreeTextInput] = useState('');
  const [assistantCardQty, setAssistantCardQty] = useState({});
  const [pendingProducts, setPendingProducts] = useState([]);
  const [currentProductContext, setCurrentProductContext] = useState(null);

  const assistantAnalyticsCtx = (userText = null) =>
    buildAssistantAnalyticsBase({
      businessId,
      currentStep,
      sessionMemory: sessionMemoryRef.current,
      servicePreference: sessionMemoryRef.current.servicePreference,
      userText,
    });

  React.useEffect(() => {
    pendingProductsRef.current = pendingProducts;
  }, [pendingProducts]);

  useAssistantChatLoad({
    routeBid,
    businessId,
    businessIdReady,
    menuReloadKey,
    lastOrder,
    navigate,
    handleMainChoiceRef,
    demoAssistantUsedRef,
    setBusinessId,
    setBusinessIdReady,
    setBusiness,
    setMenuData,
    setLoadError,
    setLoading,
    setLastOrder,
    setBenefitsProgressSnapshot,
    setMessages,
    setCurrentStep,
    setRepeatOrderAvailable,
    setCustomerId,
  });

  const baseCtx = {
    routeBid,
    businessId,
    navigate,
    menuData,
    cart,
    currentStep,
    profileDraft,
    lastOrder,
    repeatOrderAvailable,
    benefitsProgressSnapshot,
    freeTextInput,
    assistantCheckoutOpen,
    sessionMemoryRef,
    messagesRef,
    messagesEndRef,
    silentNudgeTimeoutRef,
    lastSuggestedProductIdsRef,
    demoAssistantUsedRef,
    freeTextInputRef,
    productFlowRef,
    pendingProductsRef,
    handleMainChoiceRef,
    proceedToCheckoutRef,
    showMainMenuRef,
    setProfileDraft,
    setAssistantCheckoutOpen,
    setCheckoutBootstrap,
    assistantCheckoutOpen,
    setPendingProducts,
    setCurrentProductContext,
    setCurrentStep,
    setBenefitsProgressSnapshot,
    setFreeTextInput,
    setMessages,
    assistantCardQty,
    setAssistantCardQty,
    assistantAnalyticsCtx,
    addToCart,
    addClubRewardToCart,
    mergeAddOnsIntoLatestLineForProduct,
    getCartCount,
    getCartSnapshot,
    getCartTotal,
    clearCart,
    removeFromCart,
    updateQuantity,
    getHandleMainChoice: () => wireRef.current.handleMainChoice,
  };

  const messagesApi = bindAssistantChatMessages(baseCtx);
  const checkoutApi = bindAssistantChatCheckout({
    ...baseCtx,
    ...messagesApi,
  });
  const menusApi = bindAssistantChatMenus({
    ...baseCtx,
    ...messagesApi,
    ...checkoutApi,
  });
  wireRef.current.handleMainChoice = menusApi.handleMainChoice;

  const cartApi = bindAssistantChatCart({
    ...baseCtx,
    ...messagesApi,
    ...checkoutApi,
    ...menusApi,
  });
  const searchApi = bindAssistantChatSearch({
    ...baseCtx,
    ...messagesApi,
    ...checkoutApi,
    ...menusApi,
    ...cartApi,
  });

  const { scrollToBottom } = messagesApi;

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const primaryColor = menuData?.business?.primary_color || '#18181B';
  const onAssistantHome = currentStep === 'main' && profileDraft === null;
  const showFreeTextBar = profileDraft === null;
  const messagesBottomPad = 'pb-4';

  return {
    routeBid,
    businessId,
    businessIdReady,
    loading,
    loadError,
    menuData,
    setLoadError,
    setMenuReloadKey,
    messages,
    messagesEndRef,
    messagesBottomPad,
    primaryColor,
    onAssistantHome,
    showFreeTextBar,
    repeatOrderAvailable,
    benefitsProgressSnapshot,
    getAssistantLineQty: messagesApi.getAssistantLineQty,
    bumpAssistantLineQty: messagesApi.bumpAssistantLineQty,
    getCartQtyForProductId: cartApi.getCartQtyForProductId,
    handlePostAddonAdd: cartApi.handlePostAddonAdd,
    handleCatalogProductAdd: cartApi.handleCatalogProductAdd,
    handleSuggestedProductAdd: cartApi.handleSuggestedProductAdd,
    assistantCheckoutOpen,
    setAssistantCheckoutOpen,
    checkoutBootstrap,
    setCheckoutBootstrap,
    cart,
    getCartTotal,
    clearCart,
    removeFromCart,
    updateQuantity,
    removeAddOnFromCartLine,
    getCartCount,
    profileDraft,
    setProfileDraft,
    submitProfileDraft: checkoutApi.submitProfileDraft,
    handleOrderSummaryLineQty: checkoutApi.handleOrderSummaryLineQty,
    handleOrderSummaryRemoveLine: checkoutApi.handleOrderSummaryRemoveLine,
    freeTextInput,
    setFreeTextInput,
    freeTextInputRef,
    submitFreeText: searchApi.submitFreeText,
    handleMainChoice: menusApi.handleMainChoice,
    openAssistantCheckout: checkoutApi.openAssistantCheckout,
    showCheckoutPreferencePrompt: checkoutApi.showCheckoutPreferencePrompt,
    showCartSummaryInChat: checkoutApi.showCartSummaryInChat,
    currentStep,
  };
}
